import { useState, useEffect } from 'react';
import { Image, FileImage, File, Music } from 'lucide-react';

import { API_BASE } from '../lib/apiBase';
const API_URL = API_BASE;

// Check if file is an image that can be previewed
export const isPreviewableImage = (file) => {
    const imageTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif'];
    const imageExtensions = ['.jpg', '.jpeg', '.png', '.webp', '.gif'];
    
    // Check content type
    if (file.content_type && imageTypes.includes(file.content_type.toLowerCase())) {
        return true;
    }
    
    // Check file extension
    const filename = (file.filename || file.original_filename || '').toLowerCase();
    return imageExtensions.some(ext => filename.endsWith(ext));
};

// Format file size
export const formatFileSize = (bytes) => {
    if (!bytes) return '0 B';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
};

// Get appropriate icon for file type
export const getFileIcon = (file) => {
    const filename = (file.filename || file.original_filename || '').toLowerCase();
    const contentType = (file.content_type || '').toLowerCase();
    
    if (isPreviewableImage(file)) return Image;
    if (contentType.startsWith('audio/') || filename.match(/\.(mp3|wav|flac|aac|ogg|m4a)$/)) return Music;
    if (contentType.startsWith('image/') || filename.match(/\.(pdf|ai|eps|psd|tiff?)$/)) return FileImage;
    return File;
};

// Thumbnail component that fetches image with auth
export const ArtworkThumbnail = ({ 
    file, 
    size = 'md', // 'sm' (32px), 'md' (48px), 'lg' (64px), 'xl' (96px)
    onClick,
    className = '',
    showPlaceholder = true,
    fallbackIcon: FallbackIcon = FileImage,
    fallbackColor = 'text-slate-400',
    fallbackBg = 'bg-slate-100'
}) => {
    const [imageUrl, setImageUrl] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    
    const sizeClasses = {
        sm: 'w-8 h-8',
        md: 'w-12 h-12',
        lg: 'w-16 h-16',
        xl: 'w-24 h-24'
    };
    
    const iconSizes = {
        sm: 'h-4 w-4',
        md: 'h-5 w-5',
        lg: 'h-6 w-6',
        xl: 'h-8 w-8'
    };
    
    useEffect(() => {
        let mounted = true;
        
        const fetchImage = async () => {
            if (!isPreviewableImage(file)) {
                setLoading(false);
                return;
            }
            
            try {
                const token = localStorage.getItem('token');
                const response = await fetch(`${API_URL}/api/files/${file.id}/preview`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                
                if (!response.ok) throw new Error('Failed to load preview');
                
                const contentType = response.headers.get('content-type');
                
                if (contentType && contentType.includes('application/json')) {
                    // S3 file - response contains presigned URL
                    const data = await response.json();
                    if (mounted && data.preview_url) {
                        setImageUrl(data.preview_url);
                        setLoading(false);
                    }
                } else {
                    // Local file - blob response
                    const blob = await response.blob();
                    if (mounted) {
                        const url = window.URL.createObjectURL(blob);
                        setImageUrl(url);
                        setLoading(false);
                    }
                }
            } catch (err) {
                if (mounted) {
                    setError(true);
                    setLoading(false);
                }
            }
        };
        
        fetchImage();
        
        return () => {
            mounted = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [file.id]);
    
    // Clean up object URL on unmount
    useEffect(() => {
        return () => {
            if (imageUrl) {
                window.URL.revokeObjectURL(imageUrl);
            }
        };
    }, [imageUrl]);
    
    const sizeClass = sizeClasses[size] || sizeClasses.md;
    const iconSize = iconSizes[size] || iconSizes.md;
    
    // Loading state
    if (loading && isPreviewableImage(file)) {
        return (
            <div className={`${sizeClass} rounded-md ${fallbackBg} flex items-center justify-center animate-pulse ${className}`}>
                <FallbackIcon className={`${iconSize} ${fallbackColor}`} />
            </div>
        );
    }
    
    // Error or non-image file - show placeholder
    if (error || !imageUrl) {
        if (!showPlaceholder) return null;
        
        const IconComponent = getFileIcon(file);
        return (
            <div className={`${sizeClass} rounded-md ${fallbackBg} flex items-center justify-center ${className}`}>
                <IconComponent className={`${iconSize} ${fallbackColor}`} />
            </div>
        );
    }
    
    // Image preview
    const imageElement = (
        <img 
            src={imageUrl}
            alt={file.filename || file.original_filename}
            className="w-full h-full object-cover"
            loading="lazy"
        />
    );
    
    if (onClick) {
        return (
            <button
                onClick={onClick}
                className={`${sizeClass} rounded-md overflow-hidden border border-border hover:border-slate-400 transition-colors cursor-pointer flex-shrink-0 ${className}`}
                title="Click to view/download"
            >
                {imageElement}
            </button>
        );
    }
    
    return (
        <div className={`${sizeClass} rounded-md overflow-hidden border border-border flex-shrink-0 ${className}`}>
            {imageElement}
        </div>
    );
};

// Grid view for multiple thumbnails
export const ArtworkThumbnailGrid = ({ 
    files = [], 
    onFileClick,
    maxVisible = 4,
    size = 'lg',
    className = ''
}) => {
    const visibleFiles = files.slice(0, maxVisible);
    const remainingCount = files.length - maxVisible;
    
    if (files.length === 0) return null;
    
    return (
        <div className={`flex flex-wrap gap-2 ${className}`}>
            {visibleFiles.map((file) => (
                <ArtworkThumbnail
                    key={file.id}
                    file={file}
                    size={size}
                    onClick={onFileClick ? () => onFileClick(file) : undefined}
                />
            ))}
            {remainingCount > 0 && (
                <div className={`${size === 'lg' ? 'w-16 h-16' : size === 'xl' ? 'w-24 h-24' : 'w-12 h-12'} rounded-md bg-slate-100 flex items-center justify-center text-slate-500 text-sm font-medium`}>
                    +{remainingCount}
                </div>
            )}
        </div>
    );
};

export default ArtworkThumbnail;
